// src/lib/measurement/problemFactories/length.ts

import { Rng } from '../../rng';
import { formatWithUnit, makeNearbyNumberOptions, makeLetterOptions, makeOrderOptions } from '../distractors';

export type MeasurementTopicArea = 'length' | 'mass' | 'capacity';

export type MeasurementProblemKind = 'mcq' | 'input';

export type MeasurementFigureSpec =
  | {
      kind: 'ruler';
      maxCm: 10 | 20;
      startCm: number;
      endCm: number;
      endFraction?: 0 | 0.2 | 0.8;
    }
  | {
      kind: 'containers';
      labels: Array<{ label: 'A' | 'B' | 'C'; value: string }>;
      ask: 'most' | 'least';
    }
  | {
      kind: 'balance';
      maxKg: 4;
      totalKg: number;
      itemLabel: string;
    };

export type MeasurementProblem = {
  id: string;
  kind: MeasurementProblemKind;
  type: string;
  prompt: string;
  choices?: string[];
  meta: {
    topicArea: MeasurementTopicArea;
    difficulty: 1 | 2 | 3;
    expected: string | number;
    unitHint?: string;
    templateKey: string;
    figure?: MeasurementFigureSpec;
    data?: any;
  };
};

const OBJECTS = ['pencil', 'ribbon', 'rope', 'stick', 'crayon', 'toy car'] as const;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

export function makeLengthRulerRead(rng: Rng, id: string, difficulty: 1 | 2 | 3): MeasurementProblem {
  const maxCm: 10 | 20 = difficulty === 1 ? 10 : 20;
  const object = rng.pick(OBJECTS);

  const length = rng.int(2, difficulty === 3 ? 15 : difficulty === 2 ? 12 : 9);
  const start = rng.int(0, maxCm - length);
  const end = start + length;

  const expected = formatWithUnit(length, 'cm');
  const choices = makeNearbyNumberOptions(rng, length, 'cm', 1, 4);

  return {
    id,
    kind: 'mcq',
    type: 'length_ruler_read',
    prompt: `How long is the ${object}?`,
    choices,
    meta: {
      topicArea: 'length',
      difficulty,
      expected,
      unitHint: 'cm',
      templateKey: 'length_ruler_read',
      figure: { kind: 'ruler', maxCm, startCm: start, endCm: end },
    },
  };
}

export function makeLengthRulerEstimate(rng: Rng, id: string, difficulty: 1 | 2 | 3): MeasurementProblem {
  const maxCm: 10 | 20 = difficulty === 1 ? 10 : 20;
  const object = rng.pick(OBJECTS);

  const whole = rng.int(2, difficulty === 3 ? 15 : difficulty === 2 ? 12 : 9);
  const frac: 0.2 | 0.8 = rng.pick([0.2, 0.8]);

  const start = rng.int(0, clamp(maxCm - (whole + 1), 0, maxCm));
  const endApprox = whole + frac;
  const rounded = Math.round(endApprox);

  const expected = formatWithUnit(rounded, 'cm');
  const choices = makeNearbyNumberOptions(rng, rounded, 'cm', 1, 4);

  return {
    id,
    kind: 'mcq',
    type: 'length_ruler_estimate',
    prompt: `About how long is the ${object}? (Round to the nearest cm.)`,
    choices,
    meta: {
      topicArea: 'length',
      difficulty,
      expected,
      unitHint: 'cm',
      templateKey: 'length_ruler_estimate',
      figure: { kind: 'ruler', maxCm, startCm: start, endCm: start + whole, endFraction: frac },
    },
  };
}

export function makeLengthCompareObjects(rng: Rng, id: string, difficulty: 1 | 2 | 3): MeasurementProblem {
  const askLongest = rng.chance(0.6);
  const baseMin = difficulty === 1 ? 5 : difficulty === 2 ? 10 : 20;
  const baseMax = difficulty === 1 ? 40 : difficulty === 2 ? 120 : 200;

  const lengths = rng.uniqueInts(4, baseMin, baseMax);
  const labels = ['A', 'B', 'C', 'D'] as const;

  const pairs = labels.map((l, i) => ({ l, cm: lengths[i] }));
  const best = pairs.reduce((acc, p) => (askLongest ? (p.cm > acc.cm ? p : acc) : p.cm < acc.cm ? p : acc), pairs[0]);

  const expected = best.l;
  const listText = pairs.map((p) => `${p.l}: ${p.cm} cm`).join(', ');

  return {
    id,
    kind: 'mcq',
    type: 'length_compare_objects',
    prompt: `${askLongest ? 'Which object is the longest' : 'Which object is the shortest'}? ${listText}`,
    choices: makeLetterOptions(expected),
    meta: {
      topicArea: 'length',
      difficulty,
      expected,
      templateKey: 'length_compare_objects',
      data: { pairs },
    },
  };
}

export function makeLengthOrderThree(rng: Rng, id: string, difficulty: 1 | 2 | 3): MeasurementProblem {
  const baseMin = difficulty === 1 ? 5 : difficulty === 2 ? 10 : 20;
  const baseMax = difficulty === 1 ? 60 : difficulty === 2 ? 140 : 200;

  const vals = rng.uniqueInts(3, baseMin, baseMax);
  const labels = ['A', 'B', 'C'] as const;
  const items = labels.map((l, i) => ({ l, cm: vals[i] }));

  const sorted = items.slice().sort((a, b) => a.cm - b.cm);
  const correct = `${sorted[0].l}, ${sorted[1].l}, ${sorted[2].l}`;

  const allPerms = [
    `${labels[0]}, ${labels[1]}, ${labels[2]}`,
    `${labels[0]}, ${labels[2]}, ${labels[1]}`,
    `${labels[1]}, ${labels[0]}, ${labels[2]}`,
    `${labels[1]}, ${labels[2]}, ${labels[0]}`,
    `${labels[2]}, ${labels[0]}, ${labels[1]}`,
    `${labels[2]}, ${labels[1]}, ${labels[0]}`,
  ];

  return {
    id,
    kind: 'mcq',
    type: 'length_order_three',
    prompt: `Order these lengths from smallest to largest: A: ${items[0].cm} cm, B: ${items[1].cm} cm, C: ${items[2].cm} cm`,
    choices: makeOrderOptions(rng, correct, allPerms),
    meta: {
      topicArea: 'length',
      difficulty,
      expected: correct,
      templateKey: 'length_order_three',
      data: { items },
    },
  };
}

export function makeLengthConversionCmM(rng: Rng, id: string, difficulty: 1 | 2 | 3): MeasurementProblem {
  void difficulty;
  const dir = rng.chance(0.5) ? 'cm_to_m' : 'm_to_cm';
  const cmVals = [100, 200, 300] as const;
  const mVals = [1, 2, 3] as const;

  if (dir === 'cm_to_m') {
    const cm = rng.pick(cmVals);
    return {
      id,
      kind: 'input',
      type: 'length_conversion_cm_m',
      prompt: `Convert ${cm} cm to m.`,
      meta: {
        topicArea: 'length',
        difficulty,
        expected: cm / 100,
        unitHint: 'm',
        templateKey: 'length_conversion_cm_m:cm_to_m',
        data: { dir, cm },
      },
    };
  }

  const m = rng.pick(mVals);
  return {
    id,
    kind: 'input',
    type: 'length_conversion_cm_m',
    prompt: `Convert ${m} m to cm.`,
    meta: {
      topicArea: 'length',
      difficulty,
      expected: m * 100,
      unitHint: 'cm',
      templateKey: 'length_conversion_cm_m:m_to_cm',
      data: { dir, m },
    },
  };
}

export function makePerimeterRectangleCm(rng: Rng, id: string, difficulty: 1 | 2 | 3): MeasurementProblem {
  const sideMax = difficulty === 1 ? 20 : difficulty === 2 ? 40 : 60;
  const a = rng.int(2, sideMax);
  const b = rng.int(2, sideMax);
  const perim = 2 * (a + b);

  const expected = formatWithUnit(perim, 'cm');
  const choices = makeNearbyNumberOptions(rng, perim, 'cm', difficulty === 1 ? 2 : 4, 4);

  return {
    id,
    kind: 'mcq',
    type: 'perimeter_rectangle_cm',
    prompt: `A rectangle has sides ${a} cm and ${b} cm. What is the perimeter?`,
    choices,
    meta: {
      topicArea: 'length',
      difficulty,
      expected,
      unitHint: 'cm',
      templateKey: 'perimeter_rectangle_cm',
      data: { a, b },
    },
  };
}
